import { useState } from 'react';
import hljs from 'highlight.js';
import styles from './CodeBlock.module.css';

// Extract pure string text from React children
function extractText(node) {
  if (node === null || node === undefined) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node.props?.children) return extractText(node.props.children);
  return '';
}

export default function CodeBlock({ language = 'text', children, code }) {
  const [copied, setCopied] = useState(false);

  // Get raw code string
  const rawCode = (code || extractText(children) || '').replace(/\n$/, '');

  const handleCopy = async () => {
    try {
      await window.navigator.clipboard.writeText(rawCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for restricted clipboard contexts
    }
  };

  // Perform syntax highlighting via highlight.js
  let highlightedHtml = '';
  const langKey = language?.toLowerCase().trim() || 'text';
  try {
    if (langKey && hljs.getLanguage(langKey)) {
      highlightedHtml = hljs.highlight(rawCode, { language: langKey, ignoreIllegals: true }).value;
    } else if (rawCode.length > 0) {
      highlightedHtml = hljs.highlightAuto(rawCode).value;
    } else {
      highlightedHtml = '';
    }
  } catch {
    highlightedHtml = rawCode
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.languageBadge}>{language || 'code'}</span>
        <button
          type="button"
          className={styles.copyButton}
          onClick={handleCopy}
          aria-label={copied ? 'Copied code to clipboard' : 'Copy code to clipboard'}
          title={copied ? 'Copied!' : 'Copy to clipboard'}
        >
          {copied ? (
            <>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Copied!</span>
            </>
          ) : (
            <>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className={styles.pre} tabIndex={0}>
        <code
          className={`hljs language-${langKey}`}
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
        />
      </pre>
    </div>
  );
}
